// services-pricing.ts — Services pricing engine (book: services & logistics).
//
// Graphic design (per file / per hour, exclusive) plus advanced, packaging (per element / per hour),
// logistics (multi), colour test, project management, colour calibration, installation/removal/survey
// (installation rate table + client discount), installation material, waste disposal (kg × €/kg +
// minimum delivery) and rental. Each item produces one {pv, pa, margin, blocked} line, with aggregated
// sale and purchase totals. Rush is handled separately (see pricing/discounts.ts).

import { LOGISTICS_OPTS, INSTALL_OPTS, RENTAL_OPTS, PICKUP_LOCATIONS, engineSettings } from "../../data";
import {
  DEFAULT_SERVICE_MARGIN,
  WASTE_MARGIN,
  TEST_MARGIN,
  CALIBRATION_MARGIN,
  MIN_WASTE,
  SURVEY_PV,
  SURVEY_PA,
  WASTE_PRICE_M2,
  GP_DAY_RATE,
} from "../../data";
import { num, round2 } from "../core/core";
import { engineMessage, keyParam, messageParam } from "../core/messages";
import { resolveEntryRef, serviceEntryPrice, type RateCardEntry, type DedicatedClientRateCard } from "../rate-cards/client-rate-cards";

/** Quote counters needed to compute the services (file count in addition to the quote counters). */
export interface Counters {
  fileCount: number;
  elementCount: number;
  totalM2: number;
  totalWeight: number;
}

/** Code of the « saisie manuelle » option of the rental menu: the label (`note`) and both prices are typed. */
export const MANUAL_RENTAL_CODE = "MANUAL";

/** Service line (multi: logistics, installation, rental). */
export interface ServiceQuoteLine {
  code?: string;
  quantity?: string | number;
  pv?: string | number;
  pa?: string | number;
  location?: string;
  note?: string;
  u?: string;
}

/**
 * INSTALLATION MATERIAL line — billed per m² (free-text description). An empty `m2` tracks the
 * quote's m² to produce; once entered it FREEZES. A filled `pvM2` makes the line counted; `paM2`
 * defaults to 50 % of pvM2 and is editable.
 */
export interface EquipmentLine {
  uid?: string;
  description?: string;
  m2?: string | number;
  pvM2?: string | number;
  paM2?: string | number;
}

/** Services state of a quote. */
export interface ServicesBlock {
  infoMode?: "file" | "hourly" | "off";
  infoFileRate?: string | number;
  infoHourlyRate?: string | number;
  infoHours?: string | number;
  advancedInfoOn?: boolean;
  advancedInfoHours?: string | number;
  advancedInfoHourlyRate?: string | number;
  advancedInfoText?: string;
  packagingMode?: "element" | "hourly" | "off";
  packagingElementRate?: string | number;
  packagingHourlyRate?: string | number;
  packagingHours?: string | number;
  logistics?: ServiceQuoteLine[];
  /** OUTBOUND logistics flat fee: on the CLIENT QUOTE OUTPUT (Sellsy/PDF), the filled lines collapse
   *  into ONE line "Forfait logistique pour l'ensemble des livraisons" (unit price = Σ sale prices,
   *  zero-priced collection included). NO effect on totals, margins or the summary — the computation
   *  stays line by line. */
  logisticsFlatFee?: boolean;
  /** Overall note of the OUTBOUND delivery block, shown on the client quote. */
  deliveryNote?: string;
  /** END-OF-SITE logistics: a clone of logistics (same LOGISTICS_OPTS table, same base outside
   *  agency/tier discounts — R-ASSIETTE-3). Distinct block: its flat fee never mixes with outbound. */
  endSite?: ServiceQuoteLine[];
  /** END-OF-SITE logistics flat fee: its own line "Forfait logistique fin de chantier" and its own aggregate. */
  siteEndFlatFee?: boolean;
  /** Overall note of the END-OF-SITE block. */
  endSiteNote?: string;
  testOn?: boolean;
  testPv?: string | number;
  testPa?: string | number;
  gpOn?: boolean;
  gpDays?: string | number;
  calibrationOn?: boolean;
  calibrationMaterials?: string | number;
  calibrationColours?: string | number;
  calibrationPa?: string | number;
  installation?: ServiceQuoteLine[];
  removal?: ServiceQuoteLine[];
  /** Site survey: a yes / no at a fixed price (SURVEY_PV sale, SURVEY_PA purchase). */
  surveyOn?: boolean;
  /** Date (`YYYY-MM-DD`) and time (`HH:MM`) of the survey / installation / removal: they put the operation on the planning
   *  once the quote is at 90 % or accepted. No effect on any price. */
  surveyDate?: string | null;
  surveyTime?: string | null;
  installationDate?: string | null;
  installationTime?: string | null;
  removalDate?: string | null;
  removalTime?: string | null;
  /** @deprecated Legacy multi-line survey — read ONLY so that an old quote that had survey lines keeps its survey. */
  survey?: ServiceQuoteLine[];
  /** Installation material — multiple lines billed per m² (replaces the scalar installMat*, migrated on load). */
  installEquipment?: EquipmentLine[];
  /** @deprecated Legacy scalar installation material — read ONLY to migrate to `installEquipment`. */
  installMatOn?: boolean;
  /** @deprecated */ installMatM2?: string | number;
  /** @deprecated */ installMatPvM2?: string | number;
  /** @deprecated */ installMatPa?: string | number;
  wasteOn?: boolean;
  /** @deprecated Legacy weight in kg — waste is now billed on the m² to produce. */
  wasteKg?: string | number;
  /** @deprecated Legacy price per kg — replaced by `wastePricePerM2`. */
  wastePricePerKg?: string | number;
  /** Price per m² of the waste disposal (default WASTE_PRICE_M2). */
  wastePricePerM2?: string | number;
  wastePa?: string | number;
  location?: ServiceQuoteLine[];
}

/** Computed service line. */
export interface ServiceLineCalc {
  key: string;
  label: string;
  quantity: number;
  pv: number;
  pa: number;
  total: number;
  margin: number;
  blocked: string | null;
  u: string | null;
  /** Client rate-table entry served on this service (§19) → drives the "grid" pill.
   *  Absent when the price comes from the full table or from a manual edit (which wins). */
  grid?: { entryId: string; name?: string };
  /** Title printed on the client's quote when the label carries a calculation (rates, quantities): the bare service name. */
  clientLabel?: string;
}

// DEDICATED CLIENT RATE TABLE — the registry of targetable services (§19)
//
// A "service" entry targets a CODE from this registry and carries a BASE ONLY (% of the table
// price, or a € unit price): no scale, no minimums, no showcase. OPTION codes are those of the real
// tables — the installation table is SHARED (installation, removal and survey follow the same
// entry), and so is logistics (outbound and end-of-site). FORMULA items get a synthetic code and the
// entry then applies to their unit RATE; formula minimums are left untouched. Deliberately OUTSIDE
// the registry: client collection (fixed at 0), cumulated delivery (free price), colour test, colour
// calibration, waste disposal and installation material (prices are entered, nothing to substitute).

/** A service targetable by a rate-table entry: its code, its label, its table price. */
export interface TargetableService {
  code: string;
  label: string;
  /** Full table price (unit: € per unit for an option, €/h, €/file, €/item, €/day). */
  gridPrice: number;
  unit: string;
  /** Table purchase price (0 for formula items — pure margin): the editor margin is derived from it,
   *  and the purchase price is NEVER moved by an entry. */
  gridPa: number;
}

/**
 * The COMPLETE registry of targetable services — listed by the editor (T2) and the rate card (T3).
 * @returns Every service a rate-card entry can target.
 */
export function targetableServices(): TargetableService[] {
  const out: TargetableService[] = [
    { code: "INFO_FILE", label: engineMessage("engineSvcInfoFile"), gridPrice: studioRateCard().info.file.rate, unit: "€/fichier", gridPa: 0 },
    { code: "INFO_HOURLY", label: engineMessage("engineSvcInfoHourly"), gridPrice: studioRateCard().info.hourly.rate, unit: "€/h", gridPa: 0 },
    { code: "INFO_ADVANCED", label: engineMessage("engineSvcInfoAdvanced"), gridPrice: studioRateCard().advancedInfo.rate, unit: "€/h", gridPa: 0 },
    { code: "PACKAGING_ELEMENT", label: engineMessage("engineSvcPackagingElement"), gridPrice: studioRateCard().packaging.element.rate, unit: "€/élément", gridPa: 0 },
    { code: "PACKAGING_HOURLY", label: engineMessage("engineSvcPackagingHourly"), gridPrice: studioRateCard().packaging.hourly.rate, unit: "€/h", gridPa: 0 },
    { code: "GP", label: engineMessage("engineSvcProjectManagement"), gridPrice: GP_DAY_RATE, unit: "€/jour", gridPa: 0 },
  ];
  for (const o of INSTALL_OPTS) {
    out.push({
      code: o.code,
      label: engineMessage("engineSvcInstallOpt", { option: o.label }),
      gridPrice: o.pv,
      unit: "€/créneau",
      gridPa: o.pa,
    });
  }
  for (const o of LOGISTICS_OPTS) {
    if (o.fixed_price || o.code === "CUMUL") continue; // client collection (fixed 0) and cumul (free price): nothing to substitute
    out.push({
      code: o.code,
      label: engineMessage("engineSvcLogisticsOpt", { option: o.label }),
      gridPrice: o.pv,
      unit: "€/trajet",
      gridPa: o.pa,
    });
  }
  for (const o of RENTAL_OPTS) {
    out.push({
      code: o.code,
      label: engineMessage("engineSvcRentalOpt", { option: o.label }),
      gridPrice: o.pv,
      unit: "€/location",
      gridPa: o.pa,
    });
  }
  return out;
}

/** Result of the services engine. */
export interface ServiceResult {
  lines: ServiceLineCalc[];
  servicesTotal: number;
  servicesPaTotal: number;
  /** NON-blocking installation material warnings (m² ≠ production, missing description). The quote stays valid. */
  alerts: string[];
}

/**
 * Filled in: neither the empty string nor null/undefined.
 * @param v - Value to test.
 * @returns True when the value is filled in.
 */
const isSet = (v: unknown) => v !== "" && v != null;
/**
 * Half-hour steps, floor 0.5.
 * @param hours - Raw number of hours.
 * @returns Hours rounded to the nearest half hour, at least 0.5.
 */
const roundToHalfHour = (hours: unknown) => Math.max(0.5, Math.round(num(hours) * 2) / 2);
/**
 * Margin % (1 decimal); 100 % when the sale price is zero.
 * @param pv - Sale price.
 * @param pa - Purchase price.
 * @returns The margin as a percentage.
 */
const marginPct = (pv: number, pa: number) => (pv > 0 ? Math.round((1 - pa / pv) * 1000) / 10 : 100);

/**
 * Pure migration: legacy scalar installation material (installMatOn/M2/PvM2/Pa) → a ONE-line
 * `installEquipment` array with the values preserved. The legacy purchase price was a TOTAL, so it only
 * converts faithfully to €/m² when the m² was explicit; otherwise it is left empty (50 % default).
 * Idempotent in practice: a quote already carrying `installEquipment` never goes through here.
 * @param block - Services state of the quote.
 * @returns The migrated one-line array, or `[]` when the legacy material was off.
 */
export function equipmentFromInstallMat(block: ServicesBlock): EquipmentLine[] {
  if (!block.installMatOn) return [];
  let paM2: string | number = "";
  if (isSet(block.installMatPa) && isSet(block.installMatM2) && num(block.installMatM2) > 0) paM2 = round2(num(block.installMatPa) / num(block.installMatM2));
  return [{ description: "engineInstallEquipment", m2: block.installMatM2 ?? "", pvM2: block.installMatPvM2 ?? "", paM2 }];
}

// STUDIO RATE SCHEDULE — the default rates and minimums, read from the settings.
//
// Graphic design and packaging each bill in TWO exclusive ways. The four rates and four minimums are the
// setting `quote_engine.studio_rate_card` (no value lives in the code); `computeServices` reads them here
// and they stay overridable per quote via `infoFileRate`, `infoHourlyRate`, `packagingElementRate`,
// `packagingHourlyRate`.

/** One studio billing mode: settings (`rate`, `min`) plus language-free vocabulary keys. */
export interface StudioRateEntry {
  /** Message key of the mode word (`engineStudioMode*`). */
  mode: string;
  rate: number;
  min: number;
  /** Message key of the unit word in the singular (`engineStudioUnit*`). */
  unit: string;
  /** Same as `unit` — kept for screens that already bind `unitKey`. */
  unitKey: string;
}

/**
 * The studio rate card: rates and minimums from the settings, plus message keys for each mode and unit.
 * @returns The rate card.
 */
export function studioRateCard(): {
  info: { file: StudioRateEntry; hourly: StudioRateEntry };
  packaging: { element: StudioRateEntry; hourly: StudioRateEntry };
  /** ADVANCED graphic design: always hourly and without a minimum, so it does not enter `studioCalcBase`. */
  advancedInfo: { rate: number; unit: string; unitKey: string };
} {
  const c = engineSettings().studioRateCard;
  return {
    info: {
      file: { mode: "engineStudioModeFile", unit: "engineStudioUnitFile", unitKey: "engineStudioUnitFile", ...c.info.file },
      hourly: { mode: "engineStudioModeHourly", unit: "engineStudioUnitHour", unitKey: "engineStudioUnitHour", ...c.info.hourly },
    },
    packaging: {
      element: { mode: "engineStudioModeElement", unit: "engineStudioUnitElement", unitKey: "engineStudioUnitElement", ...c.packaging.element },
      hourly: { mode: "engineStudioModeHourly", unit: "engineStudioUnitHour", unitKey: "engineStudioUnitHour", ...c.packaging.hourly },
    },
    advancedInfo: { rate: c.advancedInfo.rate, unit: "engineStudioUnitHour", unitKey: "engineStudioUnitHour" },
  };
}

/** What produced the price of a studio line. READ-BACK only — no price is recomputed here. */
export type StudioCalcBase = {
  /** Message key of the mode (`engineStudioMode*`). */
  mode: string;
  quantity: number;
  /** Message key of the unit in the singular (`engineStudioUnit*`). */
  unit: string;
  rate: number;
  /** The quantity × rate product, BEFORE the minimum. */
  gross: number;
  min: number;
  /** Did the minimum raise the price? Without this flag, "3 × 4 = 12" next to a price of 19 € reads
   *  like a bug — the most misleading case of both items. */
  minApplied: boolean;
};

/**
 * The table rate/price of a service, SERVED BY THE CLIENT ENTRY when one exists (§19) — otherwise
 * the full table price, unchanged. A single source for both the line AND the panel.
 * @param grid - Dedicated client rate card, if any.
 * @param code - Code of the targetable service.
 * @param gridPrice - Full table price.
 * @returns The rate to apply.
 */
export function clientServiceRate(grid: DedicatedClientRateCard | undefined, code: string, gridPrice: number): number {
  if (!grid) return gridPrice;
  const e = resolveEntryRef(grid, "service", code);
  return e ? serviceEntryPrice(e, gridPrice) : gridPrice;
}

/**
 * Reads the calculation mode of a studio line (`info` or `packaging`) from the quote state and the
 * quantity retained by the engine. Returns `null` when the item is off — there is then no line.
 * PURE: reads no database, decides no price, knows no display format.
 * @param block - Services state of the quote.
 * @param key - Which studio item to read.
 * @param quantity - Quantity retained by the engine (files, elements or hours).
 * @param grid - Dedicated client rate card, if any.
 * @returns What produced the price, or `null` when the item is off.
 */
export function studioCalcBase(
  block: ServicesBlock,
  key: "info" | "packaging",
  quantity: number,
  grid?: DedicatedClientRateCard,
): StudioCalcBase | null {
  // The §19 order: full table price → client entry → manual edit (a rate ENTERED on the quote wins
  // over the entry). Formula minimums are left untouched.
  const b =
    key === "info"
      ? block.infoMode === "file"
        ? { ...studioRateCard().info.file, rate: num(block.infoFileRate) || clientServiceRate(grid, "INFO_FILE", studioRateCard().info.file.rate) }
        : block.infoMode === "hourly"
          ? { ...studioRateCard().info.hourly, rate: num(block.infoHourlyRate) || clientServiceRate(grid, "INFO_HOURLY", studioRateCard().info.hourly.rate) }
          : null
      : block.packagingMode === "element"
        ? { ...studioRateCard().packaging.element, rate: num(block.packagingElementRate) || clientServiceRate(grid, "PACKAGING_ELEMENT", studioRateCard().packaging.element.rate) }
        : block.packagingMode === "hourly"
          ? { ...studioRateCard().packaging.hourly, rate: num(block.packagingHourlyRate) || clientServiceRate(grid, "PACKAGING_HOURLY", studioRateCard().packaging.hourly.rate) }
          : null;
  if (!b) return null;
  const gross = round2(b.rate * quantity);
  return { mode: b.mode, quantity, unit: b.unit, rate: b.rate, gross, min: b.min, minApplied: gross < b.min };
}

/**
 * Plural unit key for a studio quantity (hour is invariable; 0 and 1 stay singular).
 * @param quantity - Quantity to display.
 * @param unitKey - Singular unit message key.
 * @returns The unit message key to translate.
 */
function studioUnitKey(quantity: number, unitKey: string): string {
  if (unitKey === "engineStudioUnitHour" || quantity < 2) return unitKey;
  if (unitKey === "engineStudioUnitFile") return "engineStudioUnitFiles";
  if (unitKey === "engineStudioUnitElement") return "engineStudioUnitElements";
  return unitKey;
}

/**
 * THE DETAIL of the calculation, WITHOUT the mode: encoded engine message for
 * "2,5 h × 89,00 €/h", or the long form when the minimum bites.
 * @param b - What produced the price of the studio line, or `null` when the item is off.
 * @returns The encoded detail message, or `null` when there is no line.
 */
export function studioCalcDetail(b: StudioCalcBase | null): string | null {
  if (!b) return null;
  // Quantity may pluralise the unit; the rate denominator always stays singular (€/fichier).
  const unit = keyParam(studioUnitKey(b.quantity, b.unit));
  const unitRate = keyParam(b.unit);
  return b.minApplied
    ? engineMessage("engineStudioCalcDetailMin", {
        quantity: b.quantity,
        unit,
        unitRate,
        rateEur: b.rate,
        grossEur: b.gross,
        minEur: b.min,
      })
    : engineMessage("engineStudioCalcDetail", { quantity: b.quantity, unit, unitRate, rateEur: b.rate });
}

/**
 * The COMPLETE sentence, with the mode prepended to the detail. The one used by the panel.
 * @param b - What produced the price of the studio line, or `null` when the item is off.
 * @returns The encoded full sentence, or `null` when there is no line.
 */
export function studioCalcText(b: StudioCalcBase | null): string | null {
  const detail = studioCalcDetail(b);
  return detail && b
    ? engineMessage("engineStudioCalcText", { mode: keyParam(b.mode), detail: messageParam(detail) })
    : null;
}

/**
 * Computes the services of a quote. BASE (book 4.5): logistics, installation and transport sit
 * OUTSIDE agency/tier discounts and are billed at the FULL table price. Only the general discount
 * (applied at the quote footer in compute) can touch them; no client discount enters here.
 * @param block - Services state of the quote.
 * @param counters - Quote counters (files, elements, m², weight).
 * @param grid - Dedicated client rate card, if any.
 * @returns The computed service lines with their sale and purchase totals.
 */
export function computeServices(block: ServicesBlock, counters: Counters, grid?: DedicatedClientRateCard): ServiceResult {
  const out: ServiceResult = { lines: [], servicesTotal: 0, servicesPaTotal: 0, alerts: [] };
  /**
   * Pushes one computed service line and accumulates the sale and purchase totals.
   * @param key - Line key (see `serviceUnit`).
   * @param label - Displayed line label.
   * @param quantity - Billed quantity.
   * @param pv - Sale price (total of the line).
   * @param pa - Purchase price (total of the line).
   * @param margin - Margin percentage.
   * @param blocked - Blocking message, or `null`.
   * @param u - Entered unit, or `null`.
   * @param servedGrid - Client rate-card entry that served the price, if any.
   */
  const add = (key: string, label: string, quantity: number, pv: number, pa: number, margin: number, blocked: string | null = null, u: string | null = null, servedGrid?: { entryId: string; name?: string }) => {
    const total = round2(pv);
    out.lines.push({ key, label, quantity, pv: round2(pv), pa: round2(pa || 0), total, margin, blocked, u, ...(servedGrid ? { grid: servedGrid } : {}) });
    out.servicesTotal += total;
    out.servicesPaTotal += pa || 0;
  };
  /**
   * Gives the line just added a bare title for the client's quote (the calculation stays in the editor label).
   * @param message - Engine message key of the bare title.
   */
  const clientTitle = (message: string): void => {
    out.lines[out.lines.length - 1]!.clientLabel = engineMessage(message);
  };
  // DEDICATED CLIENT RATE TABLE (§19) — the order on each item: full table price → client entry →
  // manual edit (an ENTERED sale price/rate wins) → general discount (at the footer, in compute).
  // The purchase price is NEVER moved; the margin is recomputed on the client price.
  /**
   * Client rate-card entry targeting a service code, if any.
   * @param code - Code of the targetable service.
   * @returns The entry, or `undefined` when no entry serves the code.
   */
  const entryFor = (code: string): RateCardEntry | undefined => (grid ? resolveEntryRef(grid, "service", code) : undefined);
  /**
   * The "grid" pill data of a line served by a client entry.
   * @param e - The serving entry, if any.
   * @returns The pill data, or `undefined` when the price comes from the full table.
   */
  const gridBadge = (e: RateCardEntry | undefined): { entryId: string; name?: string } | undefined =>
    e ? { entryId: e.id, ...(e.name ? { name: e.name } : {}) } : undefined;

  // GRAPHIC DESIGN (per file OR per hour, exclusive)
  const infoFileRate = num(block.infoFileRate) || clientServiceRate(grid, "INFO_FILE", studioRateCard().info.file.rate);
  const infoHourlyRate = num(block.infoHourlyRate) || clientServiceRate(grid, "INFO_HOURLY", studioRateCard().info.hourly.rate);
  // ALL FOUR LABELS STATE THEIR METHOD. `studioCalcDetail` is the SAME function as the panel uses —
  // one sentence, one source (the rate it quotes is the CLIENT rate when an entry serves it).
  if (block.infoMode === "file") {
    const detail = studioCalcDetail(studioCalcBase(block, "info", counters.fileCount, grid))!;
    add("info", engineMessage("engineSvcInfoFileLabel", { detail: messageParam(detail) }), counters.fileCount, Math.max(studioRateCard().info.file.min, infoFileRate * counters.fileCount), 0, 100, null, null, num(block.infoFileRate) ? undefined : gridBadge(entryFor("INFO_FILE")));
    clientTitle("engineSvcInfoFile");
  } else if (block.infoMode === "hourly") {
    const hours = roundToHalfHour(block.infoHours);
    const detail = studioCalcDetail(studioCalcBase(block, "info", hours, grid))!;
    add("info", engineMessage("engineSvcInfoHourlyLabel", { detail: messageParam(detail) }), hours, Math.max(studioRateCard().info.hourly.min, infoHourlyRate * hours), 0, 100, null, null, num(block.infoHourlyRate) ? undefined : gridBadge(entryFor("INFO_HOURLY")));
    clientTitle("engineSvcInfoHourly");
  }
  // ADVANCED GRAPHIC DESIGN
  if (block.advancedInfoOn) {
    const hours = roundToHalfHour(block.advancedInfoHours);
    const advancedRate = num(block.advancedInfoHourlyRate) || clientServiceRate(grid, "INFO_ADVANCED", studioRateCard().advancedInfo.rate);
    add("advancedInfo", engineMessage("engineSvcInfoAdvanced"), hours, advancedRate * hours, 0, 100, null, null, num(block.advancedInfoHourlyRate) ? undefined : gridBadge(entryFor("INFO_ADVANCED")));
  }
  // PACKAGING (per element OR per hour, exclusive)
  const packagingElementRate = num(block.packagingElementRate) || clientServiceRate(grid, "PACKAGING_ELEMENT", studioRateCard().packaging.element.rate);
  const packagingHourlyRate = num(block.packagingHourlyRate) || clientServiceRate(grid, "PACKAGING_HOURLY", studioRateCard().packaging.hourly.rate);
  if (block.packagingMode === "element") {
    const detail = studioCalcDetail(studioCalcBase(block, "packaging", counters.elementCount, grid))!;
    add("packaging", engineMessage("engineSvcPackagingElementLabel", { detail: messageParam(detail) }), counters.elementCount, Math.max(studioRateCard().packaging.element.min, packagingElementRate * counters.elementCount), 0, 100, null, null, num(block.packagingElementRate) ? undefined : gridBadge(entryFor("PACKAGING_ELEMENT")));
    clientTitle("engineSvcPackagingElement");
  } else if (block.packagingMode === "hourly") {
    const hours = roundToHalfHour(block.packagingHours);
    const detail = studioCalcDetail(studioCalcBase(block, "packaging", hours, grid))!;
    add("packaging", engineMessage("engineSvcPackagingHourlyLabel", { detail: messageParam(detail) }), hours, Math.max(studioRateCard().packaging.hourly.min, packagingHourlyRate * hours), 0, 100, null, null, num(block.packagingHourlyRate) ? undefined : gridBadge(entryFor("PACKAGING_HOURLY")));
    clientTitle("engineSvcPackagingHourly");
  }
  // LOGISTICS (multi). Permanent line: no code OR quantity 0 = ignored, in the totals and the labels.
  // The logistics table is SHARED between outbound and end-of-site: one entry serves both blocks.
  for (const line of block.logistics ?? []) {
    if (!line.code) continue;
    const quantity = num(line.quantity) || 0;
    if (quantity <= 0) continue;
    const opt = LOGISTICS_OPTS.find((o) => o.code === line.code);
    if (!opt) continue;
    const logisticsEntry = opt.fixed_price ? undefined : entryFor(opt.code);
    const pvUnit = isSet(line.pv) ? num(line.pv) : opt.fixed_price ? opt.pv : clientServiceRate(grid, opt.code, opt.pv);
    const pv = pvUnit * quantity;
    const pa = opt.fixed_price ? 0 : isSet(line.pa) ? num(line.pa) : opt.pa * quantity;
    const margin = opt.fixed_price ? 100 : marginPct(pv, pa);
    const blocked = pv > 0 && pa > pv ? "engineSvcMarginLogistics" : null;
    add("logistics", engineMessage("engineSvcLogisticsOpt", { option: opt.label }), quantity, pv, pa, margin, blocked, line.u ?? null, isSet(line.pv) ? undefined : gridBadge(logisticsEntry));
  }
  // END-OF-SITE LOGISTICS (multi). Clone of logistics: same LOGISTICS_OPTS table, same base (outside
  // agency/tier discounts, the general discount being the only exception — R-ASSIETTE-3 extended).
  for (const line of block.endSite ?? []) {
    if (!line.code) continue;
    const quantity = num(line.quantity) || 0;
    if (quantity <= 0) continue;
    const opt = LOGISTICS_OPTS.find((o) => o.code === line.code);
    if (!opt) continue;
    const logisticsEntry = opt.fixed_price ? undefined : entryFor(opt.code);
    const pvUnit = isSet(line.pv) ? num(line.pv) : opt.fixed_price ? opt.pv : clientServiceRate(grid, opt.code, opt.pv);
    const pv = pvUnit * quantity;
    const pa = opt.fixed_price ? 0 : isSet(line.pa) ? num(line.pa) : opt.pa * quantity;
    const margin = opt.fixed_price ? 100 : marginPct(pv, pa);
    const blocked = pv > 0 && pa > pv ? "engineSvcMarginEndSite" : null;
    add("endSite", engineMessage("engineSvcEndSiteOpt", { option: opt.label }), quantity, pv, pa, margin, blocked, line.u ?? null, isSet(line.pv) ? undefined : gridBadge(logisticsEntry));
  }
  // COLOUR TEST (flat fee)
  if (block.testOn) {
    const pv = num(block.testPv);
    const pa = isSet(block.testPa) ? num(block.testPa) : round2(pv * (1 - TEST_MARGIN));
    add("test", engineMessage("engineSvcColourTest"), 1, pv, pa, marginPct(pv, pa));
  }
  // PROJECT MANAGEMENT — the table rate goes through the client entry when one exists; the label
  // quotes the EFFECTIVE rate (the one that makes the price).
  if (block.gpOn) {
    const days = Math.max(0.5, Math.round(num(block.gpDays) * 2) / 2);
    const gpDayRate = clientServiceRate(grid, "GP", GP_DAY_RATE);
    add("gp", engineMessage("engineSvcProjectManagementLabel", { rateDec2: gpDayRate, days }), days, gpDayRate * days, 0, 100, null, null, gridBadge(entryFor("GP")));
    clientTitle("engineSvcProjectManagement");
  }
  // COLOUR CALIBRATION — INACTIVE when materials × colours = 0 (line shown but out of totals, no block).
  if (block.calibrationOn) {
    const calibrationMaterialCount = num(block.calibrationMaterials);
    const colours = num(block.calibrationColours);
    const quantity = calibrationMaterialCount * colours;
    if (quantity > 0) {
      const pv = 75 * quantity;
      const pa = isSet(block.calibrationPa) ? num(block.calibrationPa) : round2(pv * (1 - CALIBRATION_MARGIN));
      add("calibration", engineMessage("engineSvcColourCalibration", { materials: calibrationMaterialCount, colours }), quantity, pv, pa, marginPct(pv, pa));
      clientTitle("engineSvcColourCalibrationShort");
    }
  }
  // SURVEY — yes / no at a fixed price. An old quote that carried survey lines keeps its survey.
  const surveyOn = block.surveyOn ?? (block.survey ?? []).some((l) => l.code && num(l.quantity) > 0);
  if (surveyOn) {
    add("survey", engineMessage("engineSvcSurvey"), 1, SURVEY_PV, SURVEY_PA, marginPct(SURVEY_PV, SURVEY_PA));
  }
  // INSTALLATION / REMOVAL (installation table + client discount on the table sale price)
  const installGroups: [string, string, ServiceQuoteLine[]][] = [
    ["installation", "engineSvcPose", block.installation ?? []],
    ["removal", "engineSvcRemoval", block.removal ?? []],
  ];
  for (const [key, groupKey, groupLines] of installGroups) {
    for (const line of groupLines) {
      if (!line.code) continue;
      const quantity = Math.max(0, Math.round(num(line.quantity) * 2) / 2);
      if (quantity <= 0) continue; // permanent line at quantity 0: ignored
      const opt = INSTALL_OPTS.find((o) => o.code === line.code);
      if (!opt) continue; // unknown / stale code: never bill a different option
      const pvEntered = isSet(line.pv);
      // The installation table is SHARED: one entry on a slot code serves installation, removal AND
      // survey. A hand-entered sale price wins (orange); the table purchase price is never moved.
      const pvUnit = pvEntered ? num(line.pv) : clientServiceRate(grid, opt.code, opt.pv);
      const pv = pvUnit * quantity;
      const paUnit = isSet(line.pa) ? num(line.pa) : opt.pa;
      const pa = paUnit * quantity;
      const blocked = pv > 0 && pa > pv ? `engineSvcMargin${key.charAt(0).toUpperCase()}${key.slice(1)}` : null;
      add(key, engineMessage("engineSvcInstallLine", { group: keyParam(groupKey), option: opt.label }), quantity, pv, pa, marginPct(pv, pa), blocked, line.u ?? null, pvEntered ? undefined : gridBadge(entryFor(opt.code)));
    }
  }
  // INSTALLATION MATERIAL (multi-line, billed per m²). A line is COUNTED as soon as `pvM2` is filled;
  // an empty line is never counted nor pushed. Per-line `m2`: an ENTERED value freezes, empty tracks
  // the m² to produce (counters.totalM2). `paM2` defaults to 50 % of pvM2. Base unchanged (a service: outside
  // agency discount and tiers; only the general discount touches it — R-ASSIETTE-3).
  const equipmentLines = block.installEquipment ?? equipmentFromInstallMat(block);
  let equipmentM2Sum = 0;
  let countedEquipmentCount = 0;
  for (const line of equipmentLines) {
    if (!isSet(line.pvM2)) continue; // empty line → never counted nor pushed to Sellsy
    countedEquipmentCount++;
    const m2 = isSet(line.m2) ? num(line.m2) : counters.totalM2;
    const pvM2 = num(line.pvM2);
    const pv = pvM2 * m2;
    const paM2 = isSet(line.paM2) ? num(line.paM2) : round2(pvM2 * (1 - DEFAULT_SERVICE_MARGIN));
    const pa = paM2 * m2;
    const description = String(line.description ?? "").trim();
    const isGeneric = !description || description === "engineInstallEquipment" || description === "Matériel de pose";
    const label = isGeneric
      ? engineMessage("engineInstallEquipment")
      : engineMessage("engineInstallEquipmentNamed", { name: description });
    add("installEquipment", label, m2, pv, pa, marginPct(pv, pa));
    equipmentM2Sum += m2;
    if (!description) out.alerts.push("engineSvcEquipmentNoDescription");
  }
  // NON-blocking warning: Σ m² of the filled material lines ≠ m² to produce (materials).
  if (countedEquipmentCount > 0 && round2(equipmentM2Sum) !== round2(counters.totalM2)) {
    out.alerts.push("engineSvcEquipmentM2Differs");
  }
  // WASTE DISPOSAL — billed on the m² to produce (not editable), price per m² editable (default WASTE_PRICE_M2).
  // INACTIVE at 0 m² (line shown but out of totals).
  if (block.wasteOn) {
    const m2 = counters.totalM2;
    if (m2 > 0) {
      const pricePerM2 = isSet(block.wastePricePerM2) ? num(block.wastePricePerM2) : WASTE_PRICE_M2;
      const pv = m2 * pricePerM2 + MIN_WASTE;
      const pa = isSet(block.wastePa) ? num(block.wastePa) : round2(pv * (1 - WASTE_MARGIN));
      add("waste", engineMessage("engineSvcWaste"), m2, pv, pa, marginPct(pv, pa));
    }
  }
  // RENTAL / LIFTING EQUIPMENT (multi). Permanent line: no code OR quantity 0 = ignored.
  for (const line of block.location ?? []) {
    if (!line.code) continue;
    const quantity = num(line.quantity) || 0;
    if (line.code === MANUAL_RENTAL_CODE) {
      // Typed by hand: label, quantity, sale and purchase prices are all required. Choosing the option is
      // intentional, so an incomplete line BLOCKS the validation even at quantity 0 (it is not silently ignored).
      const label = (line.note ?? "").trim();
      const pv = num(line.pv) * quantity;
      const pa = num(line.pa) * quantity;
      const incomplete = !label || quantity <= 0 || !isSet(line.pv) || !isSet(line.pa);
      const blocked = incomplete ? "engineSvcRentalManualMissing" : pv > 0 && pa > pv ? "engineSvcMarginRental" : null;
      add("rental", engineMessage("engineSvcRentalOpt", { option: label }), quantity, pv, pa, marginPct(pv, pa), blocked, line.u ?? null);
      continue;
    }
    if (quantity <= 0) continue;
    const opt = RENTAL_OPTS.find((o) => o.code === line.code);
    if (!opt) continue; // unknown / stale code: never bill a different option
    const pvUnit = isSet(line.pv) ? num(line.pv) : clientServiceRate(grid, opt.code, opt.pv);
    const pv = pvUnit * quantity;
    const pa = isSet(line.pa) ? num(line.pa) * quantity : opt.pa * quantity;
    const blocked = pv > 0 && pa > pv ? "engineSvcMarginRental" : null;
    add("rental", engineMessage("engineSvcRentalOpt", { option: opt.label }), quantity, pv, pa, marginPct(pv, pa), blocked, line.u ?? null, isSet(line.pv) ? undefined : gridBadge(entryFor(opt.code)));
  }

  // PICKUP_LOCATIONS referenced for future consistency (default place) — no direct calculation.
  void PICKUP_LOCATIONS;

  out.servicesTotal = round2(out.servicesTotal);
  out.servicesPaTotal = round2(out.servicesPaTotal);
  return out;
}
